<template>
  <modal
    elem-id="modal_credit_pack"
    :title="modalTitle"
    :show="isVisible"
    size="medium"
    :close-label="t('Fechar')"
    :save-label="t('Salvar')"
    save-test-id="credit-pack-submit"
    :save-fn="handleSave"
    :save-disabled-fn="() => formRef?.isSaving.value === true"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <credit-pack-form
        ref="formRef"
        :pack="currentPack"
        @saved="handleSaved"
        @validation-failed="() => {}"
      />

      <!-- Markets panel: shown only once the pack exists (edit mode) -->
      <template v-if="currentPackId != null">
        <div class="pack-markets-section">
          <h3 class="pack-markets-section-title" data-testid="pack-markets-section-title">
            {{ t('Preços / Mercados') }}
          </h3>
          <credit-pack-markets-panel
            :pack-id="currentPackId"
            data-testid="credit-pack-markets-panel-embedded"
            @changed="handleMarketsChanged"
          />
        </div>
      </template>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../../eventBus'
import { log } from '../../../../utils/DebugLogger'
import Modal from '../../../../ui/Modal.vue'
import CreditPackForm from './CreditPackForm.vue'
import CreditPackMarketsPanel from './CreditPackMarketsPanel.vue'
import { useCreditPacks } from '../../../../composables/useCreditPacks'
import type { AiCredits_PackRow } from '../../../../contracts/AiCredits_PackRow'

interface PackFormExposed {
  handleSubmit: () => Promise<void>
  isSaving: { value: boolean }
  resetForm: () => void
  populateFromPack: (pack: Partial<AiCredits_PackRow>) => void
}

interface ModalOpenPayload {
  pack?: Partial<AiCredits_PackRow> | null
}

const { t } = useI18n()
const { fetchPacks } = useCreditPacks()

const isVisible = ref(false)
const currentPackId = ref<number | null>(null)
/**
 * Id-less prefill for create mode — e.g. the credits-only payload the pricing
 * calculator hands off via 'modal.creditPack.open'. Not a real pack yet, so it
 * can't be resolved from the packs ref like currentPackId is.
 */
const prefillPack = ref<Partial<AiCredits_PackRow> | null>(null)
const formRef = ref<PackFormExposed | null>(null)

/** Always derived from the packs ref so it stays fresh after fetchPacks(). */
const { packs } = useCreditPacks()
const currentPack = computed<Partial<AiCredits_PackRow> | null>(() => {
  if (currentPackId.value != null) {
    return packs.value.find((p) => p.id === currentPackId.value) ?? null
  }
  return prefillPack.value
})

const modalTitle = computed(() =>
  currentPackId.value != null ? t('Editar pacote') : t('Criar pacote'),
)

function openModal(payload?: ModalOpenPayload): void {
  log('CreditPackModal', 'openModal', 'Abrindo modal', payload)
  const pack = payload?.pack ?? null
  if (pack?.id != null) {
    // Editing an existing pack — resolve it from the packs ref (currentPack stays
    // reactive to fetchPacks()).
    currentPackId.value = pack.id
    prefillPack.value = null
  } else {
    // Id-less payload (e.g. from the pricing calculator) — create mode with the
    // given fields pre-filled; there is no pack to look up yet.
    currentPackId.value = null
    prefillPack.value = pack
  }
  isVisible.value = true
}

function closeModal(): void {
  log('CreditPackModal', 'closeModal', 'Fechando modal')
  isVisible.value = false
  currentPackId.value = null
  prefillPack.value = null
}

async function handleSave(): Promise<void> {
  if (formRef.value) {
    await formRef.value.handleSubmit()
  }
}

async function handleSaved(payload: { id: number; created: boolean }): Promise<void> {
  currentPackId.value = payload.id
  await fetchPacks()
  eventBus.emit('creditPack.saved')
  eventBus.emit('creditPack.refresh')
  // Modal stays open — switches to edit mode and reveals the markets panel.
  log('CreditPackModal', 'handleSaved', 'Pack salvo, mantendo modal aberto', payload)
}

async function handleMarketsChanged(): Promise<void> {
  eventBus.emit('creditPack.refresh')
}

onMounted(() => {
  eventBus.on('modal.creditPack.open', openModal)
  eventBus.on('modal.creditPack.close', closeModal)
})

onBeforeUnmount(() => {
  eventBus.off('modal.creditPack.open', openModal)
  eventBus.off('modal.creditPack.close', closeModal)
})
</script>

<style scoped>
.pack-markets-section {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--surface-border, #e0e0e0);
}

.pack-markets-section-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 12px 0;
}
</style>
